import { Fragment, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Dialog, Disclosure, Tab, Transition } from "@headlessui/react";
import { constants } from "../utils/ranges";
import { ReactComponent as Chevron } from "../assets/icons/Chevron.svg";
import { ReactComponent as Info } from "../assets/icons/Info.svg";
import { ReactComponent as Caret } from "../assets/icons/Caret.svg";
import { ReactComponent as HbA1cOptimal } from "../assets/icons/HbA1cOptimal.svg";
import { ReactComponent as HbA1cNonOptimal } from "../assets/icons/HbA1cNonOptimal.svg";
import Slider from "react-slick";

const Range = ({
  ranges = [
    { point: 0, label: "Start", color: "#ccc" },
    { point: 100, label: "End", color: "#000" },
  ],
  selectedIndex,
  className = "",
}) => {
  selectedIndex -= ranges.length - 1 === selectedIndex;
  return (
    <div className={`relative grid grid-flow-col auto-cols-fr ${className}`}>
      {ranges.slice(0, -1).map((rangeItem, index, list) => (
        <div key={`range-${index}`} className="flex flex-col justify-end">
          {index === selectedIndex && (
            <Caret
              className="self-center shrink-0 w-2 h-1"
              style={{ color: ranges[selectedIndex].color }}
            />
          )}
          <h6
            className="opacity-60 text-center text-xxs font-medium uppercase truncate"
            style={
              index === selectedIndex
                ? { color: ranges[selectedIndex].color }
                : {}
            }
          >
            {rangeItem.label}
          </h6>
          <div
            className="my-1 h-1"
            style={{
              backgroundColor:
                index === selectedIndex
                  ? ranges[selectedIndex].color
                  : "#d4d8e2",
              borderTopLeftRadius: index === 0 ? 9999 : undefined,
              borderBottomLeftRadius: index === 0 ? 9999 : undefined,
              borderTopRightRadius:
                index === list.length - 1 ? 9999 : undefined,
              borderBottomRightRadius:
                index === list.length - 1 ? 9999 : undefined,
            }}
          />
          <p className="opacity-70 text-xxs">{rangeItem.point}</p>
        </div>
      ))}
      <p className="absolute bottom-0 right-0 opacity-70 text-xxs">
        {ranges[ranges.length - 1].point}
      </p>
    </div>
  );
};

const ListItemContainer = ({
  disabled = false,
  label = "",
  defaultExpanded = false,
  title,
  desc,
  className = "",
  ...props
}) => {
  return (
    <Disclosure
      defaultOpen={defaultExpanded}
      as="div"
      className={`px-4 py-4 rounded-[20px] border border-white bg-[#F9F9F9] text-black ${className}`}
    >
      <Disclosure.Button as="div" disabled={disabled}>
        {({ open }) => [
          <div
            key="list-item-container-head"
            className="flex items-start justify-between space-x-1"
          >
            <h2 className="text-sm font-medium text-[#4D6698]">{label}</h2>
            <Chevron
              className={`h-3 w-3 opacity-70 transition-transform duration-300 ${
                open ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>,
          <Fragment key="list-item-container-body">{props.children}</Fragment>,
        ]}
      </Disclosure.Button>
      <Disclosure.Panel>
        {typeof title === "string" && (
          <h5 className="mt-2.5 text-[#4D6698] text-xs font-medium">{title}</h5>
        )}
        {typeof desc === "string" && (
          <p className="mt-1.5 text-xxs font-light leading-tight whitespace-pre-line">
            {desc}
          </p>
        )}
      </Disclosure.Panel>
    </Disclosure>
  );
};

const CardiovascularObservationResult = ({ result }) => {
  const [modalState, setModalState] = useState({
    visible: false,
    title: "",
    desc: "",
  });

  const closeModal = () =>
    setModalState({ visible: false, title: "", desc: "" });

  return result?.scan_id?.length > 0 ? (
    <section className="pt-0 overflow-hidden">
      <div className="px-6 pb-8 tab-screen">
        <div className="circle-blur w-[70%] h-64 mx-auto absolute left-0 right-0 top-16 blur-[100px]"></div>
        <Slider
          dots
          infinite
          slidesToShow={1}
          slidesToScroll={1}
          initialSlide={0}
          autoplay
          autoplaySpeed={2000}
          className="instruction-slide"
        >
          <div className="slider-item">
            <div className="score-card px-5 py-5 w-full">
              <div className="flex flex-row justify-between items-center mb-3">
                <div className="card-tittle  w-12/12">
                  <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
                    Overall Health Score
                  </h4>
                </div>
              </div>
              <p className=" text-xxs text-white/70 text-justify mt-1 min-h-20">
                Your Vitals Score combines multiple assessments including acute
                physiology monitoring, cardiovascular risk evaluation, metabolic
                control, autonomic stress analysis, body composition, and
                hemodynamic measurements for comprehensive health assessment.
              </p>
              <div className="progress-col relative mt-10 mb-1">
                <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${result?.wellness_score ?? 0}%`,
                    }}
                  >
                    <div className="progress-value">
                      {Math.floor(result?.wellness_score ?? 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="slider-item">
            <div className="score-card px-5 py-5 w-full">
              <div className="flex flex-row justify-between items-center mb-3">
                <div className="card-tittle w-12/12">
                  <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
                    Cardiac Score
                  </h4>
                </div>
              </div>
              <p className=" text-xxs text-white/70 text-justify mt-1 min-h-16">
                Comprehensive risk score of heart disease through the facial
                scan looking at your vascular strains, heart rate, blood
                pressure and heart rate variability risk.
              </p>
              <div className="progress-col relative mt-10 mb-1">
                <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${result?.metadata?.overall_heart_score ?? 0}%`,
                    }}
                  >
                    <div className="progress-value">
                      {Math.floor(result?.metadata?.overall_heart_score ?? 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="slider-item">
            <div className="score-card px-5 py-5 w-full">
              <div className="flex flex-row justify-between items-center mb-3">
                <div className="card-tittle  w-12/12">
                  <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
                    Metabolic Control Score
                  </h4>
                </div>
              </div>
              <p className=" text-xxs text-white/70 text-justify mt-1 min-h-16">
                Assesses your likelihood of developing type 2 diabetes based on
                the facial scan, age, body weight, and metabolic indicators.
              </p>
              <div className="progress-col relative mt-10 mb-1">
                <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${
                        result?.metadata?.glucose_info
                          ?.diabetes_control_score ?? 0
                      }%`,
                    }}
                  >
                    <div className="progress-value">
                      {Math.floor(
                        result?.metadata?.glucose_info
                          ?.diabetes_control_score ?? 0
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="slider-item">
            <div className="score-card px-5 py-5 w-full">
              <div className="flex flex-row justify-between items-center mb-3">
                <div className="card-tittle  w-12/12">
                  <h4 className="text-xl font-semibold leading-5 from-[#455C8E] via-[#455C8E] to-[#A7D4FF] bg-gradient-to-r bg-clip-text text-transparent">
                    Autonomic Stress Score
                  </h4>
                </div>
              </div>
              <p className=" text-xxs text-white/70 text-justify mt-1 min-h-16">
                Estimates your risk of cognitive decline by analyzing
                physiological parameters, parasympathetic and sympathetic stress
                computed via face scan coupled with Baevsky stress index.
              </p>
              <div className="progress-col relative mt-10 mb-1">
                <div className="progress bg-gradient-to-l from-[#64BD40] via-[#FFC444] to-[#C81616]">
                  <div
                    className="progress-bar"
                    style={{
                      width: `${
                        result?.metadata?.mental_wellbeing_score ?? 0
                      }%`,
                    }}
                  >
                    <div className="progress-value">
                      {Math.floor(
                        result?.metadata?.mental_wellbeing_score ?? 0
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Slider>
      </div>
      <div className="bg-white pt-6 pb-12">
        <div className="tab-screen">
          <h2 className="mb-4 mx-6 text-black text-lg font-medium">
            Detailed Report
          </h2>
          <Tab.Group>
            <Tab.List className="py-1 flex items-start justify-start overflow-x-auto tab-link relative px-5">
              {["Key Body Vitals", "Heart Health", "Physiological"].map(
                (label, index) => (
                  <Tab
                    key={`tab-header-${index}`}
                    className={({ selected }) =>
                      `m-1 px-4 py-2 rounded-full border border-white ${
                        selected
                          ? "bg-[#EFF4FF] text-[#3F6FD2]"
                          : "bg-[#eee] text-[#000000]"
                      } text-xs font-medium whitespace-nowrap outline-none`
                    }
                  >
                    {label}
                  </Tab>
                )
              )}
              <Tab
                key="tab-header-4"
                className={({ selected }) =>
                  `relative m-1 px-4 py-2 rounded-full border border-white ${
                    selected
                      ? "bg-[#EFF4FF] text-[#3F6FD2]"
                      : "bg-[#eee] text-[#000000]"
                  } text-xs font-medium whitespace-nowrap outline-none`
                }
              >
                Blood Biomarkers
                <span className="absolute right-3 top-0 -translate-y-1/2 px-1 py-0.5 rounded-full bg-error text-white text-[0.5625rem] leading-none font-semibold">
                  BETA
                </span>
              </Tab>
              {(result?.metadata?.heart_scores?.stress_index ?? "--") !==
                "--" && (
                <Tab
                  key="tab-header-5"
                  className={({ selected }) =>
                    `m-1 px-4 py-2 rounded-full border border-white ${
                      selected
                        ? "bg-[#EFF4FF] text-[#3F6FD2]"
                        : "bg-[#eee] text-[#000000]"
                    } text-xs font-medium whitespace-nowrap outline-none`
                  }
                >
                  Stress & ANS
                </Tab>
              )}
            </Tab.List>
            <Tab.Panels className="px-6 my-6 space-y-6">
              <Tab.Panel className="grid lg:grid-cols-2 grid-cols-1 gap-4 items-stretch">
                {[
                  { label: "Heart Rate", value: result?.vitals.heart_rate },
                  {
                    label: "Respiration Rate",
                    value: result?.vitals.resp_rate,
                  },
                  {
                    label: "Oxygen Saturation",
                    value: result?.vitals.oxy_sat_prcnt,
                  },
                ].map((item, index) => {
                  const ranges =
                    item.label === "Heart Rate"
                      ? constants[item.label].ranges[
                          result?.posture ?? "resting"
                        ]
                      : constants[item.label].ranges;
                  const selectedRangeIndex = ranges.reduce(
                    (p, c, ci) => (item.value >= c.point ? ci : p),
                    0
                  );
                  return (
                    <ListItemContainer
                      key={`section-item-${index}`}
                      label={item.label}
                      title={`Your ${item.label} is ${ranges[selectedRangeIndex].label}`}
                      desc={
                        constants[item.label].info +
                        "\n\n" +
                        ranges[selectedRangeIndex].desc
                      }
                    >
                      <div className="py-3 flex items-center justify-between space-x-2">
                        <div className="shrink-0 basis-1/4 text-center font-semibold">
                          <h4 className="text-primary text-xl">{item.value}</h4>
                          <h5 className="opacity-70 text-xxs">
                            {constants[item.label].unit}
                          </h5>
                        </div>
                        <Range
                          className="grow"
                          ranges={ranges}
                          selectedIndex={selectedRangeIndex}
                        />
                      </div>
                    </ListItemContainer>
                  );
                })}
                {(() => {
                  const sys = result?.vitals.bp_sys;
                  const dia = result?.vitals.bp_dia;
                  const selectedSYSRangeIndex =
                    sys !== "--"
                      ? constants["Blood Pressure"].ranges[0].reduce(
                          (p, c, ci) => (sys > c.point ? ci : p),
                          0
                        )
                      : undefined;
                  const selectedDIARangeIndex =
                    dia !== "--"
                      ? constants["Blood Pressure"].ranges[1].reduce(
                          (p, c, ci) => (dia > c.point ? ci : p),
                          0
                        )
                      : undefined;
                  return (
                    <ListItemContainer
                      key="section-item-3"
                      label="Blood Pressure"
                      title={`Your Blood Pressure is ${constants["Blood Pressure"].ranges[0][selectedSYSRangeIndex]?.label}`}
                      desc={
                        constants["Blood Pressure"].info +
                        "\n\n" +
                        constants["Blood Pressure"].ranges[0][
                          selectedSYSRangeIndex
                        ]?.desc
                      }
                      disabled={
                        selectedSYSRangeIndex === undefined ||
                        selectedDIARangeIndex === undefined
                      }
                    >
                      <div className="py-3 flex items-center justify-between space-x-2">
                        <div className="shrink-0 basis-1/4 text-center">
                          <div className="font-semibold">
                            <h4 className="inline text-primary text-xl">
                              {sys}
                            </h4>
                            &nbsp;
                            <h5 className="inline opacity-70 text-xxs">
                              {constants["Blood Pressure"].unit}
                            </h5>
                          </div>
                          <p className="text-ternary text-xxs font-medium">
                            Systolic
                          </p>
                        </div>
                        <Range
                          className="grow blood-pressure"
                          ranges={constants["Blood Pressure"].ranges[0]}
                          selectedIndex={selectedSYSRangeIndex}
                        />
                      </div>
                      <div className="py-3 flex items-center justify-between space-x-2">
                        <div className="shrink-0 basis-1/4 text-center">
                          <div className="font-semibold">
                            <h4 className="inline text-primary text-xl">
                              {dia}
                            </h4>
                            &nbsp;
                            <h5 className="inline opacity-70 text-xxs">
                              {constants["Blood Pressure"].unit}
                            </h5>
                          </div>
                          <p className="text-ternary text-xxs font-medium">
                            Diastolic
                          </p>
                        </div>
                        <Range
                          className="grow"
                          ranges={constants["Blood Pressure"].ranges[1]}
                          selectedIndex={selectedDIARangeIndex}
                        />
                      </div>
                    </ListItemContainer>
                  );
                })()}
              </Tab.Panel>
              <Tab.Panel className="grid lg:grid-cols-2 grid-cols-1 gap-4">
                {[
                  {
                    label: "SDNN",
                    value:
                      result?.metadata.heart_scores.sdnn !== "--"
                        ? Math.round(result?.metadata.heart_scores.sdnn)
                        : "--",
                  },
                  {
                    label: "RMSSD",
                    value:
                      result?.metadata.heart_scores.rmssd !== "--"
                        ? Math.round(result?.metadata.heart_scores.rmssd)
                        : "--",
                  },
                  {
                    label: "PNN50",
                    value: result?.metadata.heart_scores.pNN50_per,
                  },
                  {
                    label: "Cardiac Output",
                    value: result?.metadata.cardiovascular.cardiac_out,
                  },
                  {
                    label: "Mean Arterial Pressure",
                    value: result?.metadata.cardiovascular.map,
                  },
                  {
                    label: "Heart Utilized",
                    value: result?.metadata.heart_scores.heart_utilized,
                  },
                ].map((item, index) => {
                  const selectedRangeIndex =
                    item.value !== "--"
                      ? constants[item.label].ranges.reduce(
                          (p, c, ci) => (item.value >= c.point ? ci : p),
                          0
                        )
                      : undefined;
                  return (
                    <ListItemContainer
                      key={`section-item-${index}`}
                      label={item.label}
                      title={`Your ${item.label} is ${
                        constants[item.label].ranges[selectedRangeIndex]?.label
                      }`}
                      desc={
                        constants[item.label].info +
                        "\n\n" +
                        constants[item.label].ranges[selectedRangeIndex]?.desc
                      }
                      disabled={selectedRangeIndex === undefined}
                    >
                      <div className="py-3 flex items-center justify-between space-x-2">
                        <div className="shrink-0 basis-1/4 text-center font-semibold">
                          <h4 className="text-primary text-xl">{item.value}</h4>
                          <h5 className="opacity-70 text-xxs">
                            {constants[item.label].unit}
                          </h5>
                        </div>
                        <Range
                          className="grow"
                          ranges={constants[item.label].ranges}
                          selectedIndex={selectedRangeIndex}
                        />
                      </div>
                    </ListItemContainer>
                  );
                })}
                <div className="grid grid:grid-cols-4 grid-cols-2 gap-3 col-span-full">
                  {[
                    {
                      label: "Heart Rate Max",
                      value: result?.metadata.heart_scores.HRMax,
                      gradient: "from-[#ebf8fd] to-[#ebf8fd]",
                    },
                    {
                      label: "Heart Rate Reserve",
                      value: result?.metadata.heart_scores.HRR,
                      gradient: "from-[#ebf8fd] to-[#ebf8fd]",
                    },
                    {
                      label: "Target HR Range",
                      value: result?.metadata.heart_scores.THRR,
                      gradient: "from-[#fff4f3]/60 to-[#fff4f3]",
                    },
                    {
                      label: "VO₂ Max",
                      value: result?.metadata.physiological_scores.vo2max,
                      gradient: "from-[#fff4f3]/60 to-[#fff4f3]",
                    },
                  ].map((item, index) => (
                    <button
                      type="button"
                      key={`section-smallitem-${index}`}
                      className={`px-3 py-4 rounded-xl bg-gradient-to-b ${item.gradient} flex flex-col items-stretch justify-between text-left`}
                      onClick={() =>
                        setModalState({
                          visible: true,
                          title: item.label,
                          desc: constants[item.label].description,
                        })
                      }
                    >
                      <div className="flex items-start justify-between space-x-1">
                        <h2 className="text-xs font-medium text-primary">
                          {item.label}
                        </h2>
                        <Info className="my-0.5 h-3 w-3 opacity-70 text-primary" />
                      </div>
                      <div className="mt-3 break-all font-semibold">
                        <h4 className="inline text-primary text-xl">
                          {item.value}
                        </h4>
                        &nbsp;
                        <h5 className="inline opacity-70 text-xxs text-primary">
                          {constants[item.label].unit}
                        </h5>
                      </div>
                    </button>
                  ))}
                </div>
              </Tab.Panel>
              <Tab.Panel className="grid grid:grid-cols-2 grid-cols-1 gap-4">
                {(() => {
                  const bmi = result?.metadata.physiological_scores.bmi;
                  const selectedBMIRangeIndex = constants.BMI.ranges.reduce(
                    (p, c, ci) => (bmi > c.point ? ci : p),
                    0
                  );
                  return (
                    <ListItemContainer
                      label="BMI"
                      title={`Your BMI is ${constants.BMI.ranges[selectedBMIRangeIndex].label}`}
                      desc={
                        constants.BMI.info +
                        "\n\n" +
                        constants.BMI.ranges[selectedBMIRangeIndex].desc
                      }
                    >
                      <div className="py-3 flex items-center justify-between space-x-2">
                        <div className="shrink-0 basis-1/4 text-center font-semibold">
                          <h4 className="text-primary text-xl">{bmi}</h4>
                          <h5 className="opacity-70 text-xxs text-primary">
                            {constants.BMI.unit}
                          </h5>
                        </div>
                        <Range
                          className="grow"
                          ranges={constants.BMI.ranges}
                          selectedIndex={selectedBMIRangeIndex}
                        />
                      </div>
                    </ListItemContainer>
                  );
                })()}

                <div className="grid grid-cols-2 gap-4">
                  {[
                    {
                      label: "Blood Volume",
                      value: result?.metadata.physiological_scores.bloodvolume,
                      gradient: "from-[#ebf8fd] to-[#ebf8fd]",
                    },
                    {
                      label: "Total Body Water",
                      value: result?.metadata.physiological_scores.tbw,
                      gradient: "from-[#ebf8fd] to-[#ebf8fd]",
                    },
                    ...(result?.metadata.physiological_scores.cal_carb !==
                      "--" &&
                    result?.metadata.physiological_scores.cal_fat !== "--"
                      ? [
                          {
                            label: "Calorie from Carb",
                            value:
                              !(
                                result?.metadata.physiological_scores
                                  .cal_carb >= 0
                              ) ||
                              !(
                                result?.metadata.physiological_scores
                                  .cal_carb <= 100
                              )
                                ? "--"
                                : result.metadata.physiological_scores.cal_carb,
                            gradient: "from-[#dff5e3] to-[#dff5e3]",
                          },
                          {
                            label: "Calorie from Fat",
                            value:
                              !(
                                result?.metadata.physiological_scores.cal_fat >=
                                0
                              ) ||
                              !(
                                result?.metadata.physiological_scores.cal_fat <=
                                100
                              )
                                ? "--"
                                : result.metadata.physiological_scores.cal_fat,
                            gradient: "from-[#fdf4f3] to-[#fdf4f3]",
                          },
                        ]
                      : []),
                  ].map((item, index) => (
                    <button
                      type="button"
                      key={`section-smallitem-${index}`}
                      className={`px-3 py-4 rounded-xl bg-gradient-to-b ${item.gradient} flex flex-col items-stretch justify-between text-left`}
                      onClick={() =>
                        setModalState({
                          visible: true,
                          title: item.label,
                          desc: constants[item.label].description,
                        })
                      }
                    >
                      <div className="flex items-start justify-between space-x-1">
                        <h2 className="text-xs font-medium text-primary">
                          {item.label}
                        </h2>
                        <Info className="my-0.5 h-3 w-3 opacity-70 text-primary" />
                      </div>
                      <div className="mt-3 break-all font-semibold">
                        <h4 className="inline text-primary text-xl">
                          {item.value}
                        </h4>
                        &nbsp;
                        <h5 className="inline opacity-70 text-xxs text-primary">
                          {constants[item.label].unit}
                        </h5>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-4 text-black col-span-full">
                  {[
                    {
                      label: "Body Water %",
                      value: result?.metadata.physiological_scores.tbwp,
                    },
                    {
                      label: "Total Body Fat",
                      value: result?.metadata.physiological_scores.bodyfat,
                    },
                    {
                      label: "Body Weight",
                      value: result?.metadata.physiological_scores.weight,
                    },
                  ].map((item, index) => (
                    <button
                      type="button"
                      key={`section-smallitem-${index}`}
                      className="px-3 py-4 rounded-xl bg-gradient-to-b from-[#fff4f3]/60 to-[#fff4f3] flex flex-col items-stretch justify-between text-left"
                      onClick={() =>
                        setModalState({
                          visible: true,
                          title: item.label,
                          desc: constants[item.label].description,
                        })
                      }
                    >
                      <div className="flex items-start justify-between space-x-1">
                        <h2 className="text-xs font-medium text-primary">
                          {item.label}
                        </h2>
                        <Info className="my-0.5 h-3 w-3 opacity-70 text-primary" />
                      </div>
                      <div className="mt-3 break-all font-semibold">
                        <h4 className="inline text-primary text-xl">
                          {item.value}
                        </h4>
                        &nbsp;
                        <h5 className="inline opacity-70 text-xxs">
                          {constants[item.label].unit}
                        </h5>
                      </div>
                    </button>
                  ))}
                </div>
              </Tab.Panel>
              <Tab.Panel className="space-y-6">
                {(() => {
                  if (typeof result?.metadata?.glucose_info?.hba1c !== "number")
                    return null;

                  const hba1c = result?.metadata.glucose_info.hba1c;
                  return (
                    <div className="px-4 py-4 rounded-[20px] border border-[#e5ecfb] bg-[#222635] text-white relative">
                      <div className="hba-shadow absolute right-2 bottom-2"></div>
                      <div className="flex items-center justify-between space-x-4 text-[#3F6FD2] relative z-50">
                        {hba1c < 5.7 ? (
                          <HbA1cOptimal height={36} width={36} />
                        ) : (
                          <HbA1cNonOptimal height={36} width={36} />
                        )}
                        <div className="grow">
                          <h2 className="text-sm font-semibold text-white">
                            Predicted HbA1c Level
                          </h2>
                          <h4 className="mt-1 text-xs font-medium text-secondary">
                            {hba1c < 5.7 ? "Optimal" : "Above Optimal"}
                          </h4>
                        </div>
                        <div className="shrink-0 text-right flex flex-row items-end font-semibold text-secondary">
                          <h4 className="text-lg">
                            {hba1c < 5.7 ? "<5.7" : "≥5.7"}
                          </h4>
                          <h5 className="opacity-70 text-xxs ml-1">
                            {constants["HbA1c"].unit}
                          </h5>
                        </div>
                      </div>
                      <div className="relative z-50 my-1 py-4 mx-8 grid grid-flow-col auto-cols-fr">
                        <div
                          style={{
                            color: constants["HbA1c"].ranges[0].color,
                          }}
                        >
                          <h6 className="opacity-60 text-left text-xxs font-medium uppercase truncate">
                            {constants["HbA1c"].ranges[0].label}
                          </h6>
                          <div className="my-1 h-1 bg-current rounded-l-full" />
                        </div>
                        <div
                          style={{
                            color: constants["HbA1c"].ranges[1].color,
                          }}
                        >
                          <h6 className="opacity-60 text-center text-xxs font-medium uppercase truncate">
                            {constants["HbA1c"].ranges[1].label}
                          </h6>
                          <div className="my-1 h-1 bg-current rounded-none" />
                          <p className="ml-1 !text-white/70 text-xs">
                            {constants["HbA1c"].ranges[1].point}
                          </p>
                        </div>
                        <div
                          style={{
                            color: constants["HbA1c"].ranges[2].color,
                          }}
                        >
                          <h6 className="opacity-60 text-right text-xxs font-medium uppercase truncate">
                            {constants["HbA1c"].ranges[2].label}
                          </h6>
                          <div className="my-1 h-1 bg-current rounded-r-full" />
                          <p className="-ml-2 !text-white/70 text-xs">
                            {constants["HbA1c"].ranges[2].point}
                          </p>
                        </div>
                        <div className="absolute left-1/3 top-0 bottom-0 border-l-2 border-dashed border-white/60" />
                        <svg
                          className={`absolute left-1/3 top-0.5 h-2.5 ${
                            hba1c < 5.7
                              ? "-translate-x-[250%] rotate-180"
                              : "translate-x-[150%]"
                          }`}
                          viewBox="0 0 6 11"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M0.5 0.5L0.5 10.5L5.5 5.42754L0.5 0.5Z"
                            fill="#3F6FD2"
                          />
                        </svg>
                      </div>
                      <p className="text-xxs leading-3 text-white mt-2 relative z-50">
                        {hba1c < 5.7
                          ? "Your HbA1c level is within the optimal range (<5.7%). Maintain a healthy lifestyle to support long-term well-being."
                          : "Your HbA1c level appears to be above the optimal range (≥5.7%). We recommend getting a comprehensive test or consulting a healthcare professional for proper evaluation and guidance."}
                      </p>
                    </div>
                  );
                })()}
              </Tab.Panel>
              {(result?.metadata?.heart_scores?.stress_index ?? "--") !==
                "--" && (
                <Tab.Panel className="space-y-6">
                  {(() => {
                    const stressLevel =
                      result?.metadata.heart_scores.stress_index;
                    const selectedRangeIndex = constants[
                      "Stress Level"
                    ].ranges.reduce(
                      (p, c, ci) => (stressLevel > c.point ? ci : p),
                      0
                    );
                    const selectedRange =
                      constants["Stress Level"].ranges[selectedRangeIndex];
                    let detailTitle = "";
                    let detailDescription = "";
                    if (
                      (result?.metadata?.heart_scores?.rmssd ?? "--") !== "--"
                    ) {
                      if (result.metadata.heart_scores.rmssd < 65) {
                        if (
                          result.vitals.heart_rate > 100 ||
                          result.vitals.bp_sys > 130 ||
                          result.vitals.bp_dia > 90
                        ) {
                          detailTitle = "More Sympathetic Interactions";
                          detailDescription =
                            "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating).\n\nThe sympathetic nervous system (often called “fight or flight”) reflects responses to things like stress and exercise, and increases your heart rate and blood pressure.";
                        } else {
                          detailTitle = "More Parasympathetic Interactions";
                          detailDescription =
                            "Although HRV manifests as a function of your heart rate, it actually originates from your nervous system. Your autonomic nervous system, which controls the involuntary aspects of your physiology, has two branches, parasympathetic (deactivating) and sympathetic (activating)\n\nThe parasympathetic nervous system (often referred to as “rest and digest”) handles inputs from internal organs, like digestion or your fingernails and hair growing.";
                        }
                      } else {
                        detailTitle = "Balanced ANS Interactions";
                        detailDescription =
                          "When you have high heart rate variability, it means that your body is responsive to both sets of inputs (parasympathetic and sympathetic). This is a sign that your nervous system is balanced, and that your body is very capable of adapting to its environment and performing at its best.";
                      }
                    } else {
                      detailDescription =
                        "Not enought data to process. This happens when the signal strength of the scan is poor due to improper light or too much movements.";
                    }
                    return (
                      <>
                        <div className="px-4 py-4 rounded-[20px] border border-[#f1e5cd] bg-[#232831] text-white relative">
                          <div className="stress-shadow absolute right-2 bottom-2"></div>
                          <div className="flex items-center justify-between space-x-4 relative z-50">
                            {selectedRange.icon}
                            <div className="grow">
                              <h2 className="text-sm font-semibold">
                                Stress Level
                              </h2>
                              <h4
                                className="mt-1 text-xxs font-semibold uppercase"
                                style={{ color: selectedRange.color }}
                              >
                                {selectedRange.label}
                              </h4>
                            </div>
                            <div className="shrink-0 text-right font-semibold">
                              <h4 className="text-secondary text-xl">
                                {stressLevel}
                              </h4>
                              <h5 className="opacity-90 text-xxs">
                                {constants["Stress Level"].unit}
                              </h5>
                            </div>
                          </div>
                          <div className="mt-4">
                            <h5 className="text-secondary text-xs font-medium">
                              Your Stress Level is {selectedRange.label}
                            </h5>
                            <p className="mt-1.5 text-xxs leading-tight whitespace-pre-line">
                              {constants["Stress Level"].description}
                            </p>
                          </div>
                        </div>
                        <div className="rounded-[20px] bg-[#d9eefe] space-y-1.5 pt-4 overflow-hidden">
                          {detailTitle.length > 0 && (
                            <h3 className="text-sm font-semibold text-primary px-4">
                              {detailTitle}
                            </h3>
                          )}
                          <p className="text-xxs leading-tight whitespace-pre-line text-primary px-4 pb-2">
                            {detailDescription}
                          </p>
                          <h5 className="text-xs text-center font-semibold text-primary bg-[#b9d1e4] p-2">
                            Autonomic Nervous Systems (ANS)
                          </h5>
                        </div>
                      </>
                    );
                  })()}
                </Tab.Panel>
              )}
              <div className="p-5 rounded-[20px] flex flex-col bg-[#364560] text-white">
                <h4 className="text-sm font-medium text-[#8AB2DF]">
                  Disclaimer
                </h4>
                <p className="mt-2 text-xxs text-justify leading-4 text-white/75">
                  For Investigational Use Only. CarePlix is not a substitute for
                  the clinical judgment of a healthcare professional. CarePlix
                  is intended to improve your awareness of general wellness.
                  CarePlix does not diagnose, treat, mitigate or prevent any
                  disease, symptom, disorder or abnormal physical state. Consult
                  with a healthcare professional or emergency services if you
                  believe you may have a medical issue.
                </p>
                <Link
                  className="mt-4 px-4 py-2 rounded-full bg-white text-primary text-xs font-medium w-28 mx-auto text-center"
                  to={-2}
                  replace
                >
                  Go Back
                </Link>
              </div>
            </Tab.Panels>
          </Tab.Group>
        </div>
      </div>
      <Transition appear show={modalState.visible} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/70" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-[90%] max-w-md transform overflow-hidden rounded-2xl bg-white px-4 py-8 text-center align-middle shadow-xl transition-all">
                  <h3 className="text-primary text-sm font-semibold">
                    {modalState.title}
                  </h3>
                  <p className="mt-4 text-primary/70 text-xs leading-tight">
                    {modalState.desc}
                  </p>
                  <button
                    className="mt-6 px-8 py-2 rounded-full outline-none bg-primary text-white text-sm"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </section>
  ) : (
    <Navigate to="/" replace />
  );
};

export default CardiovascularObservationResult;
